import { Point, LineSegment } from '@models/connector';

export function parseSVGPath(svgElement): Point[] {
    const pathString = svgElement.getAttribute('d');
    const transformString = svgElement.getAttribute('transform');
    const commands = pathString.match(/[A-Za-z][^A-Za-z]*/g) || [];
    const segments: LineSegment[] = [];

    let lastPoint: Point | null = null;
    let lastControlPoint: Point | null = null;

    let rotation: { angle: number; cx: number; cy: number } | null = null;
    if (transformString) {
        const rotateMatch = transformString.match(
            /rotate\(\s*([-\d.]+)(?:deg)?\s*[,\s]\s*([-\d.]+)\s*[,\s]\s*([-\d.]+)\s*\)/
        );
        if (rotateMatch) {
            rotation = {
                angle: parseFloat(rotateMatch[1]),
                cx: parseFloat(rotateMatch[2]),
                cy: parseFloat(rotateMatch[3]),
            };
        }
    }

    function applyRotation(point: Point): Point {
        if (!rotation) return point;
        const rad = (rotation.angle * Math.PI) / 180;
        const cos = Math.cos(rad);
        const sin = Math.sin(rad);
        const dx = point.x - rotation.cx;
        const dy = point.y - rotation.cy;
        return {
            x: rotation.cx + dx * cos - dy * sin,
            y: rotation.cy + dx * sin + dy * cos,
        };
    }

    for (const command of commands) {
        const commandType = command.charAt(0);
        const params = command
            .substring(1)
            .trim()
            .split(/\s*,\s*|\s+/)
            .filter(s => s.length > 0)
            .map(parseFloat);

        switch (commandType) {
            case 'M':
                lastPoint = { x: params[0], y: params[1] };
                break;
            case 'L':
                if (lastPoint) {
                    const endPoint = { x: params[0], y: params[1] };
                    segments.push({ start: lastPoint, end: endPoint });
                    lastPoint = endPoint;
                }
                break;
            case 'Q':
                if (lastPoint) {
                    const controlPoint = { x: params[0], y: params[1] };
                    const endPoint = { x: params[2], y: params[3] };
                    segments.push({ start: lastPoint, end: controlPoint });
                    segments.push({ start: controlPoint, end: endPoint });
                    lastPoint = endPoint;
                    lastControlPoint = controlPoint;
                }
                break;
            case 'C':
                if (lastPoint) {
                    const controlPoint1 = { x: params[0], y: params[1] };
                    const controlPoint2 = { x: params[2], y: params[3] };
                    const endPoint = { x: params[4], y: params[5] };
                    segments.push({ start: lastPoint, end: controlPoint1 });
                    segments.push({ start: controlPoint1, end: controlPoint2 });
                    segments.push({ start: controlPoint2, end: endPoint });
                    lastPoint = endPoint;
                    lastControlPoint = controlPoint2;
                }
                break;
            default:
                break;
        }
    }

    const gripPoints = calculatePoints(segments, applyRotation);

    return gripPoints;
}

function calculatePoints(segments: LineSegment[], applyRotation: (point: Point) => Point): Point[] {
    const gripPoints: Point[] = [];

    function isDuplicate(point: Point): boolean {
        return gripPoints.some(p => p.x === point.x && p.y === point.y);
    }

    for (const segment of segments) {
        const startPoint = applyRotation({ x: segment.start.x, y: segment.start.y });
        const middlePoint = applyRotation({
            x: (segment.start.x + segment.end.x) / 2,
            y: (segment.start.y + segment.end.y) / 2,
        });
        const endPoint = applyRotation({ x: segment.end.x, y: segment.end.y });

        if (!isDuplicate(startPoint)) {
            gripPoints.push(startPoint);
        }
        gripPoints.push(middlePoint);
        if (!isDuplicate(endPoint)) {
            gripPoints.push(endPoint);
        }
    }

    return gripPoints;
}

export function parseTriangleSVGPath(svgElement): Point[] {
    const pathString = svgElement.getAttribute('d');
    const transformString = svgElement.getAttribute('transform');
    const commands = pathString.match(/[A-Za-z][^A-Za-z]*/g) || [];

    let lastPoint: Point | null = null;

    const edges: { from: Point; cp: Point | null; to: Point }[] = [];
    let firstPoint: Point | null = null;

    for (const command of commands) {
        const commandType = command.charAt(0);
        const params = command
            .substring(1)
            .trim()
            .split(/\s*,\s*|\s+/)
            .filter(s => s.length > 0)
            .map(parseFloat);

        switch (commandType) {
            case 'M':
                lastPoint = { x: params[0], y: params[1] };
                firstPoint = lastPoint;
                break;
            case 'L':
                if (lastPoint) {
                    const to = { x: params[0], y: params[1] };
                    edges.push({ from: lastPoint, cp: null, to });
                    lastPoint = to;
                }
                break;
            case 'Q':
                if (lastPoint) {
                    const cp = { x: params[0], y: params[1] };
                    const to = { x: params[2], y: params[3] };
                    edges.push({ from: lastPoint, cp, to });
                    lastPoint = to;
                }
                break;
            case 'Z':
                if (lastPoint && firstPoint && (lastPoint.x !== firstPoint.x || lastPoint.y !== firstPoint.y)) {
                    edges.push({ from: lastPoint, cp: null, to: firstPoint });
                }
                break;
            default:
                break;
        }
    }

    let rotation: { angle: number; cx: number; cy: number } | null = null;
    if (transformString) {
        const rotateMatch = transformString.match(
            /rotate\(\s*([-\d.]+)(?:deg)?\s*[,\s]\s*([-\d.]+)\s*[,\s]\s*([-\d.]+)\s*\)/
        );
        if (rotateMatch) {
            rotation = {
                angle: parseFloat(rotateMatch[1]),
                cx: parseFloat(rotateMatch[2]),
                cy: parseFloat(rotateMatch[3]),
            };
        }
    }

    function applyRotation(point: Point): Point {
        if (!rotation) return point;
        const rad = (rotation.angle * Math.PI) / 180;
        const cos = Math.cos(rad);
        const sin = Math.sin(rad);
        const dx = point.x - rotation.cx;
        const dy = point.y - rotation.cy;
        return {
            x: rotation.cx + dx * cos - dy * sin,
            y: rotation.cy + dx * sin + dy * cos,
        };
    }

    function quadraticBezierMidpoint(from: Point, cp: Point, to: Point): Point {
        return {
            x: 0.25 * from.x + 0.5 * cp.x + 0.25 * to.x,
            y: 0.25 * from.y + 0.5 * cp.y + 0.25 * to.y,
        };
    }

    const points: Point[] = [];

    for (const edge of edges) {
        if (edge.cp) {
            points.push(applyRotation(quadraticBezierMidpoint(edge.from, edge.cp, edge.to)));
        } else {
            points.push(
                applyRotation({
                    x: (edge.from.x + edge.to.x) / 2,
                    y: (edge.from.y + edge.to.y) / 2,
                })
            );
        }
    }

    return points;
}
